import { useMutation } from "@apollo/client/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { constantsData, identifiersData, namesData, statusTypesData } from "@/data";
import { deleteReleaseMotionArtworkMutation, getReleaseMotionArtworksListQuery } from "@/graph";
import { openAlert } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const useDeleteReleaseMotionArtwork = (releaseId) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const [deleteReleaseMotionArtworkAction, { loading }] = useMutation(deleteReleaseMotionArtworkMutation, {
        awaitRefetchQueries: true,
        refetchQueries: [
            {
                query: getReleaseMotionArtworksListQuery,
                variables: {
                    where: {
                        release: {
                            id: { equals: releaseId },
                        },
                    },
                },
            },
        ],
    });

    const { deleteReleaseMotionArtworkPending: deleteReleaseMotionArtworkPendingToastName } = namesData.toasts;

    const { toast: toastId } = identifiersData.ids;

    const deleteReleaseMotionArtwork = async (id) => {
        try {
            await deleteReleaseMotionArtworkAction({
                onCompleted: () => {
                    dispatch(
                        openAlert(
                            t("messages.deleteMsgs.success", {
                                entity: `${t("releases.entity")} ${t("labels.motionArtwork")}`,
                            }),
                            statusTypesData.success,
                        ),
                    );
                },
                variables: {
                    where: { id },
                },
            });
        } catch (err) {
            displayErrorHandler(
                err.message === constantsData.failedToFetch ? t("messages.networkFailure") : err.message,
                dispatch,
                openAlert,
            );
        } finally {
            toast.dismiss(`${deleteReleaseMotionArtworkPendingToastName}_${toastId}`);
        }
    };

    useEffect(
        () => {
            if (loading) {
                toast.info(
                    () =>
                        t("messages.deleteMsgs.pending", {
                            entity: `${t("releases.entity")} ${t("labels.motionArtwork")}`,
                        }),
                    { toastId: `${deleteReleaseMotionArtworkPendingToastName}_${toastId}` },
                );
            }
        },
        [loading], //eslint-disable-line
    );

    return { deleteReleaseMotionArtwork };
};

export default useDeleteReleaseMotionArtwork;
